/**
 * ShippingLabelsTable – renders shipping labels data with CustomerName, CustomerState, CustomerCity
 * 
 * Features:
 * - Renders shipping labels data with CustomerName, CustomerState, CustomerCity
 * - Search controls, sorting, expansion, download menus
 * - Implements its own search and sort logic
 * 
 * Props expected:
 * - selectedDocument: document data with pages
 * - documentFilename: filename for title display
 * - expandedRows: expansion state
 * - toggleRow: row expansion handler
 * - anchorEl: menu state
 * - handleMenuOpen, handleMenuClose: menu handlers
 * - downloadFiles: download utility function
 * 
 * Author: Galo Alonso Peralta Espinoza  
 * Date: 04-17-2025
 */

import React, { useState, useMemo } from 'react'

// Material UI
import { Typography, Select, MenuItem, Menu, Tooltip } from '@mui/material'
import IconButton from '@mui/material/IconButton'

// Material UI Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DownloadIcon from '@mui/icons-material/Download'

// Local CSS
import styles from './ShippingLabelsTable.module.css'

/**
 * Shipping Labels Table Component
 * Renders table for shipping labels with CustomerName, CustomerState, CustomerCity
 */
export default function ShippingLabelsTable({
  selectedDocument,
  documentFilename,
  expandedRows,
  toggleRow,
  anchorEl,
  handleMenuOpen,
  handleMenuClose,
  downloadFiles
}) {
  // State for search and sort
  const [searchTerm, setSearchTerm] = useState('')
  const [searchAttribute, setSearchAttribute] = useState('Any')
  const [sortColumn, setSortColumn] = useState('page_number')
  const [sortDirection, setSortDirection] = useState('asc')

  // Ensure expandedRows is always a Set for consistent API
  const expandedRowsSet = expandedRows instanceof Set ? expandedRows : new Set(expandedRows)

  // Setting up the document title
  const documentTitle = documentFilename ? documentFilename : 'Shipping Labels Dataframe'

  // Attribute options for search
  const attributeOptions = useMemo(() => {
    if (!selectedDocument?.pages) return ['page_number']

    const keys = new Set(['page_number'])
    selectedDocument.pages.forEach(p => {
      Object.keys(p.entities || {}).forEach(k => keys.add(k))
    })

    return Array.from(keys)
  }, [selectedDocument])

  // Filtered and sorted pages
  const displayedPages = useMemo(() => {
    if (!selectedDocument?.pages) return [];

    // 1) Exact Matching (Quotes) vs Partial
    const isExact = /^['"].*['"]$/.test(searchTerm);
    const termRaw = isExact
      ? searchTerm.slice(1, -1)
      : searchTerm.toLowerCase();

    // 2) Build Field List
    const fields = searchAttribute === 'Any'
      ? attributeOptions
      : [searchAttribute];

    // 3) Get Value helper
    const getValue = (page, col) => {
      if (col === 'page_number') return page.page_number;
      const arr = page.entities[col];
      return Array.isArray(arr) ? arr.join(' ') : '';
    };

    // 4) Filter by Term + Attribute
    let pages = selectedDocument.pages.filter(page => {
      if (!termRaw) return true;
      return fields.some(col => {
        const valStr = String(getValue(page, col));
        return isExact
          ? valStr === termRaw                // exact match
          : valStr.toLowerCase().includes(termRaw); // partial
      });
    });

    // 5) Sort by Selected Column
    pages.sort((a, b) => {
      const va = getValue(a, sortColumn)
      const vb = getValue(b, sortColumn)
    
      // Empty or null go last
      const emptyA = va === '' || va == null
      const emptyB = vb === '' || vb == null
      if (emptyA && emptyB) return 0
      if (emptyA) return 1   // a after b
      if (emptyB) return -1  // b after a
    
      // Numeric compare for page_number
      if (sortColumn === 'page_number') {
        return sortDirection === 'asc' 
          ? va - vb 
          : vb - va 
      }
    
      // String compare (case-insensitive)
      const aStr = String(va).toLowerCase()
      const bStr = String(vb).toLowerCase()
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1
      if (aStr > bStr) return sortDirection === 'asc' ?  1 : -1
      return 0
    })

    return pages
  }, [
    selectedDocument,
    searchTerm,
    searchAttribute,
    sortColumn,
    sortDirection,
    attributeOptions
  ])

  //** Rendering Expanded Content **//
  const renderExpandedDetails = (allEntities) => {
    const keys = Object.keys(allEntities)
    const mid = Math.ceil(keys.length / 2)
    const leftKeys = keys.slice(0, mid)
    const rightKeys = keys.slice(mid)

    return (
      <div className={styles.expandedDetails}>
        <div className={styles.column}>
          {leftKeys.map((k) => (
            <div key={k} className={styles.entityBox}>
              <div className={styles.entityKey}>{k}:</div>
              <div className={styles.entityValue}>{allEntities[k].join(', ')}</div>
            </div>
          ))}
        </div>
        <div className={styles.column}>
          {rightKeys.map((k) => (
            <div key={k} className={styles.entityBox}>
              <div className={styles.entityKey}>{k}:</div>
              <div className={styles.entityValue}>{allEntities[k].join(', ')}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tableContainer}>
      {/* Section Title */}
      <Typography variant="h6" className={styles.sectionTitle}>
        {documentTitle}
      </Typography>

      {/* Table Controls */}
      <div className={styles.tableControls}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Search Bar */}
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {/* Search Attribute Selector */}
          <Select
            value={searchAttribute}
            onChange={e => setSearchAttribute(e.target.value)}
            size="small"
            style={{ marginLeft: 8, height: '2rem', fontSize: '1rem', borderRadius: 5 }}
          >
            <MenuItem value="Any">Any</MenuItem>
            {attributeOptions.map(attr => (
              <MenuItem key={attr} value={attr}>
                {attr === 'page_number'
                  ? 'Page Number'
                  : attr.replace(/([A-Z])/g, ' $1').trim()}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div style={{ position: 'relative', textAlign: 'center' }}>
          <Tooltip title="Download" arrow>
            <IconButton 
              className={styles.downloadIcon} 
              onClick={handleMenuOpen}
              disabled={!selectedDocument}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MenuItem onClick={() => {
              downloadFiles(
                [selectedDocument.id],
                'csv',
                () => documentFilename.replace(/\.pdf$/i,''),
                () => selectedDocument.model || 'shipping-labels',
                false,
                'full-csv'
              )
              handleMenuClose()
            }}>
              Full CSV
            </MenuItem>
            <Tooltip title="Simple CSV download currently not supported for shipping labels">
              <span>
                <MenuItem disabled>Simple CSV</MenuItem>
              </span>
            </Tooltip>
            <MenuItem onClick={() => {
              downloadFiles(
                [selectedDocument.id],
                'json',
                () => documentFilename.replace(/\.pdf$/i,'')
              )
              handleMenuClose()
            }}>
              JSON
            </MenuItem>
          </Menu>
        </div>
      </div>

      {/* Shipping Labels Dataframe Table */}
      <table className={styles.dataframeTable}>
        {/* Table Header */}
        <thead>
          <tr>
            {/* Column Headers - Shipping Labels Specific */}
            {[
              { key: 'page_number',   label: 'Page Number'  },
              { key: 'CustomerName',  label: 'Customer Name' },
              { key: 'CustomerState', label: 'Customer State'},
              { key: 'CustomerCity',  label: 'Customer City' }
            ].map(col => (
              <th
                key={col.key}
                onClick={() => {
                  if (sortColumn === col.key) {
                    setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'))
                  } else {
                    setSortColumn(col.key)
                    setSortDirection('asc')
                  }
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {col.label}
                {sortColumn === col.key && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        {/* Table Body */}
        <tbody>
          {displayedPages.map((page, index) => {
            const isExpanded = expandedRowsSet.has(index);
                
            return (
              <React.Fragment key={index}>
                {/* Main Table Row */}
                <tr onClick={(e) => toggleRow(index, e)} className={styles.topRow}>
                  <td>{page.page_number}</td>
                  <td>{page.entities.CustomerName ? page.entities.CustomerName.join(', ') : ''}</td>
                  <td>{page.entities.CustomerState ? page.entities.CustomerState.join(', ') : ''}</td>
                  <td>{page.entities.CustomerCity ? page.entities.CustomerCity.join(', ') : ''}</td>
                  <td style={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={(e) => toggleRow(index, e)}
                      className={styles.expandIcon}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </td>
                </tr>
                {/* Collapsible Table Logic */}
                {isExpanded && (
                  <tr className={styles.expandRow}>
                    <td colSpan={5} style={{ padding: 0 }}>
                      {renderExpandedDetails(page.entities)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  )
} 